// URL search params shared by the inbox and thread routes, so Escape returns to the same list.
// The split lives here (a legacy view, or a label id from the rail) and so does the search
// scope, so a reload lands on the same list.
import type { ThreadListView } from "@web/lib/endpoints";

export interface ListSearch {
  view?: Exclude<ThreadListView, "inbox"> | undefined;
  /** A Gmail label id chosen in the rail (INBOX is the default and never carried). */
  label?: string | undefined;
  account?: string | undefined;
  q?: string | undefined;
  /** "gmail": the query goes to Gmail's own search for the account (default: the local cache). */
  scope?: "gmail" | undefined;
}

const VIEWS = new Set(["all", "unread", "starred"]);
const LABEL_ID = /^[^\s"\\]{1,200}$/;

export function parseListSearch(raw: Record<string, unknown>): ListSearch {
  const out: ListSearch = {};
  if (typeof raw.view === "string" && VIEWS.has(raw.view)) {
    out.view = raw.view as ListSearch["view"];
  }
  if (typeof raw.label === "string" && LABEL_ID.test(raw.label) && raw.label !== "INBOX") {
    out.label = raw.label;
    out.view = undefined;
  }
  if (typeof raw.account === "string" && raw.account.trim()) out.account = raw.account.trim();
  if (typeof raw.q === "string" && raw.q.trim()) out.q = raw.q.trim().slice(0, 500);
  if (raw.scope === "gmail" && out.q) out.scope = "gmail";
  return out;
}

export function viewOf(search: ListSearch): ThreadListView {
  return search.view ?? "inbox";
}

/** Stable identity for a list (used to remember scroll, selection and row order). */
export function listIdentity(search: ListSearch): string {
  if (search.q) return `search:${search.q}|${search.scope ?? "local"}|${search.account ?? "*"}`;
  if (search.label) return `label:${search.label}|${search.account ?? "*"}`;
  return `${viewOf(search)}|${search.account ?? "*"}`;
}

/** Toggle a filter view: pressing `u` on the unread list returns to the inbox. */
export function toggleView(search: ListSearch, view: "unread" | "starred"): ListSearch {
  const next: ListSearch = { ...search, q: undefined, scope: undefined, label: undefined };
  next.view = search.view === view ? undefined : view;
  return next;
}

/** Show one label (the rail): a user label belongs to one account, so that account is kept. */
export function withLabel(
  search: ListSearch,
  label: string | undefined,
  account: string | undefined = search.account,
): ListSearch {
  return {
    account,
    label: label === "INBOX" ? undefined : label,
    view: undefined,
    q: undefined,
    scope: undefined,
  };
}

export function withAccount(search: ListSearch, account: string | undefined): ListSearch {
  return { ...search, account };
}

/** Is this list the inbox split (the default, or INBOX named explicitly)? */
export function isInboxSplit(search: ListSearch): boolean {
  return !search.q && !search.label && !search.view;
}

/** Only same-app paths are allowed as post-login destinations. */
export function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return undefined;
  if (value.startsWith("/login") || value.startsWith("/setup")) return undefined;
  return value;
}
